import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { AlertCircle, Calendar, CheckCircle, Clock } from 'lucide-react';

export default function StandupsPage() {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD
  });

  // Fetch standup notes for selected date
  const { data, isLoading, error } = useQuery({
    queryKey: ['standups', selectedDate],
    queryFn: async () => {
      const res = await api.get('/api/standups', {
        params: { date: selectedDate },
      });
      return res.data;
    },
  });

  // Fetch all employees to compare submitted vs not submitted
  const { data: usersData } = useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const res = await api.get('/api/users', { params: { role: 'EMPLOYEE' } });
      return res.data.data || res.data || [];
    },
  });

  const notes = data?.data || [];
  const employees = Array.isArray(usersData) ? usersData : [];

  // Get set of employees who submitted
  const submittedIds = new Set(notes.map((n) => n.user.id));

  // Get those who haven't submitted
  const notSubmitted = employees.filter((e) => !submittedIds.has(e.id));

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const formatTime = (iso) => {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const dateLabel = new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Daily Updates
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          View team standup notes by date
        </p>
      </div>

      {/* Date Picker */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 border border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-500" />
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">{dateLabel}</span>
        </div>
      </div>

      {/* Stats */}
      {!isLoading && !error && employees.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-5 flex items-start gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 bg-green-50 text-green-600 dark:bg-slate-700 dark:text-green-400">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Submitted</p>
              <p className="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white">{notes.length}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">of {employees.length} employees</p>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-5 flex items-start gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 bg-orange-50 text-orange-600 dark:bg-slate-700 dark:text-orange-400">
              <Clock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Not Submitted</p>
              <p className="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white">{notSubmitted.length}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">pending submission</p>
            </div>
          </div>
        </div>
      )}

      {/* Submitted Notes */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Submitted Updates ({notes.length})
        </h2>

        <div className="grid gap-4">
          {isLoading && (
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">Loading updates...</p>
            </div>
          )}

          {error && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-900/30 p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-300">Error loading updates</p>
                <p className="text-sm text-red-700 dark:text-red-400">{error.message}</p>
              </div>
            </div>
          )}

          {!isLoading && !error && notes.length === 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-8 text-center border border-dashed border-gray-300 dark:border-slate-700">
              <p className="text-gray-500 dark:text-gray-400">
                No updates submitted yet for this date.
              </p>
            </div>
          )}

          {!isLoading && !error && notes.length > 0 && (
            notes.map((note) => (
              <div
                key={note.id}
                className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start gap-4 mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {note.user?.name || 'Unknown'}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTime(note.createdAt)}
                    </p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                </div>

                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words border border-gray-200 dark:border-slate-600">
                  {note.note}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Not Submitted */}
      {!isLoading && !error && notSubmitted.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Pending Updates ({notSubmitted.length})
          </h2>

          <div className="grid gap-3">
            {notSubmitted.map((emp) => (
              <div
                key={emp.id}
                className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                  <span className="font-medium text-gray-900 dark:text-white">{emp.name}</span>
                </div>
                <span className="text-xs font-semibold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-slate-700 px-2.5 py-1 rounded">
                  Not Submitted
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
